import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Endpoint, EndpointType } from 'utils/enums/endpoint.util';
import { OrganizationContextFilter } from 'utils/interfaces/context-filter.util';
import type {
  InviteeLookup,
  OrgMemberListItem,
} from 'utils/interfaces/org-member.util';
import {
  ExtendedUser,
  UserWithTenantAndOrganization,
} from 'utils/interfaces/prisma-combined-types.util';
import { GenericService } from './generic.service';

@Injectable({ providedIn: 'root' })
export class UserService extends GenericService<UserWithTenantAndOrganization> {

  /**
   * The current organization's members — the list behind every "who can I pick?"
   * surface (assignee, watcher, @mentions, group membership). Unlike `fetchAll`,
   * this never widens for tenant or platform admins.
   *
   * The org is passed in rather than read from `this.params()`: the caller reacts
   * to the context changing, and reading the ambient signal here would race that
   * same change and fetch the previous org's members.
   */
  fetchOrganizationMembers(
    contextFilter: OrganizationContextFilter
  ): Observable<UserWithTenantAndOrganization[]> {
    return this.http.get<UserWithTenantAndOrganization[]>(
      `api/${Endpoint.USER}/${EndpointType.ALL}/${EndpointType.MEMBERS}`,
      { params: this.paramsPipe.transform(contextFilter) }
    );
  }

  /**
   * The org-admin Members listing: every non-revoked membership of the org in
   * context (including org-deactivated ones), joined with the member. The org
   * is threaded in explicitly for the same reason as `fetchOrganizationMembers`
   * — callers react to the context changing.
   */
  fetchOrgMembersForAdmin(
    contextFilter: OrganizationContextFilter
  ): Observable<OrgMemberListItem[]> {
    return this.http.get<OrgMemberListItem[]>(
      `api/${Endpoint.USER}/${EndpointType.MEMBERS}/manage`,
      { params: this.paramsPipe.transform(contextFilter) }
    );
  }

  fetchUserByOrganization(): Observable<UserWithTenantAndOrganization> {
    return this.http.get<UserWithTenantAndOrganization>(
      `api/${Endpoint.USER}/all`,
      { params: this.params() }
    );
  }

  fetchCurrentUser(): Observable<ExtendedUser> {
    return this.http.get<ExtendedUser>(`api/${Endpoint.USER}`);
  }

  /**
   * Users the resource can be shared with. Carries the active-org contextFilter:
   * without it the backend falls back to the caller's full visibility, which for
   * an uber admin is every user on the platform.
   */
  fetchAllToShareWith(
    resourceId: string
  ): Observable<UserWithTenantAndOrganization[]> {
    return this.http.get<UserWithTenantAndOrganization[]>(
      `api/${Endpoint.USER}/all/${resourceId}/share`,
      { params: this.params() }
    );
  }

  resendInvite(userId: string): Observable<{ email: string }> {
    return this.http.post<{ email: string }>(
      `api/${Endpoint.USER}/${userId}/resend-invite`,
      {}
    );
  }

  /**
   * The invite dialog's email pre-check: whether the address already belongs
   * to a platform user (invite = membership grant) and their names for
   * autofill. Gated backend-side by the same boundary as the invite.
   */
  lookupInvitee(
    email: string,
    organizationId: string
  ): Observable<InviteeLookup> {
    return this.http.get<InviteeLookup>(
      `api/${Endpoint.USER}/invite-lookup`,
      { params: { email, organizationId } }
    );
  }
}
